import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, SectionTitle, Spinner } from "@/components/common";
import { useLoad, useTrip } from "@/context/TripContext";
import { api, downloadFile, errMsg, fmtDate } from "@/lib/api";

export default function Docs() {
  const { tid, isOrg } = useTrip();
  const [docs, reload] = useLoad(`/trips/${tid}/documents`);
  const [title, setTitle] = useState("");
  const [vis, setVis] = useState("all");
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  if (!docs) return <Spinner />;
  const upload = async () => {
    const fl = file.current?.files?.[0];
    if (!fl) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", fl); fd.append("title", title); fd.append("visibility", vis);
    setBusy(true);
    try { await api.post(`/trips/${tid}/documents`, fd); toast.success("Uploaded"); setTitle(""); file.current.value = ""; reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (d) => { if (!window.confirm(`Delete ${d.title}?`)) return; try { await api.delete(`/trips/${tid}/documents/${d.doc_id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-5">
      <SectionTitle title="Documents & trip info" sub="Files are private to trip members. 'Organizers only' files are hidden from travelers." />
      {isOrg && (
        <div className="card flex flex-wrap items-end gap-3 p-5" data-testid="doc-upload-form">
          <Input ref={file} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.docx,.xlsx" className="max-w-xs" data-testid="doc-file-input" />
          <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} className="max-w-xs" data-testid="doc-title-input" />
          <select className="nselect max-w-[180px]" value={vis} onChange={(e) => setVis(e.target.value)} data-testid="doc-visibility-select"><option value="all">All travelers</option><option value="organizers">Organizers only</option></select>
          <Button className="rounded-full" onClick={upload} disabled={busy} data-testid="doc-upload-button"><Upload className="mr-1 h-4 w-4" />{busy ? "Uploading…" : "Upload"}</Button>
          <p className="w-full text-xs text-slate-500">PDF, images, text, CSV, Word or Excel up to 10 MB. Avoid uploading passports unless necessary.</p>
        </div>
      )}
      {docs.length === 0 ? <EmptyState icon={FileText} title="No documents yet" text={isOrg ? "Upload booking confirmations, house rules or the welcome guide." : "Organizers haven't shared any files yet."} /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="doc-list">
          {docs.map((d) => (
            <div key={d.doc_id} className="card flex items-center gap-3 p-4" data-testid={`doc-${d.doc_id}`}>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-100 font-mono text-xs font-bold uppercase text-teal-700">{d.ext}</span>
              <div className="min-w-0 flex-1"><p className="truncate font-semibold">{d.title}</p><p className="text-xs text-slate-500">{(d.size / 1024).toFixed(0)} KB · {fmtDate(d.created_at)}{d.visibility === "organizers" && " · organizers only"}</p></div>
              <Button size="icon" variant="ghost" onClick={() => downloadFile(`/trips/${tid}/documents/${d.doc_id}/download`, d.filename).catch((e) => toast.error(errMsg(e)))} aria-label="Download" data-testid={`doc-download-${d.doc_id}`}><Download className="h-4 w-4" /></Button>
              {isOrg && <Button size="icon" variant="ghost" onClick={() => del(d)} aria-label="Delete" data-testid={`doc-delete-${d.doc_id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
